import clsx from 'clsx';
import styles from 'components/UI/V3/Dropdown/Content/DropdownContent.module.scss';
import { useDropdown } from 'components/UI/V3/Dropdown/DropdownContext';
import { DropdownFooter } from 'components/UI/V3/Dropdown/Footer/DropdownFooter';
import MobileBottomModal from 'components/UI/V3/MobileBottomModal';
import React from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';

export interface DropdownContentProps {
    children: React.ReactNode;
    className?: string;
    /** Optional z-index for the dropdown portal content / mobile sheet backdrop */
    zIndex?: number;
    /** The minimum width of the content */
    minWidth?: number;
    /** The maximum width of the content */
    maxWidth?: number;
    /** The maximum height of the content. Default is 300 */
    maxHeight?: number;
    /** The preferred side of the trigger to render against. Default is 'bottom' */
    side?: 'top' | 'right' | 'bottom' | 'left';
    /** The preferred alignment against the trigger. Default is 'center' */
    align?: 'start' | 'center' | 'end';
    /** The distance in pixels from the trigger. Default is 8 */
    sideOffset?: number;
    /** The distance in pixels from the start/end of the trigger. Default is 0 */
    alignOffset?: number;
    /** The padding between the arrow and the edges of the content. Default is 10 */
    collisionPadding?: number;
    /** Show bottom modal on mobile. Default is true */
    showBottomModalOnMobile?: boolean;
    /** Optional props forwarded to MobileBottomModal */
    mobileBottomModalProps?: {
        className?: string;
        backdropClassName?: string;
    };
}

export const DropdownContent: React.FC<DropdownContentProps> = ({
    children,
    className = '',
    zIndex,
    minWidth,
    maxWidth,
    maxHeight,
    side = 'bottom',
    align = 'center',
    sideOffset = 8,
    alignOffset = 0,
    collisionPadding = 10,
    showBottomModalOnMobile = true,
    mobileBottomModalProps
}) => {
    const { open, setOpen, triggerRef } = useDropdown();
    const contentRef = React.useRef<HTMLDivElement>(null);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const isMobile = useMediaQuery({ query: '(max-width:600px)' });

    // Check if DropdownFooter is present in children
    const hasFooter = React.useMemo(() => {
        return React.Children.toArray(children).some(child => {
            return React.isValidElement(child) && child.type === DropdownFooter;
        });
    }, [children]);

    const calculatePosition = React.useCallback(() => {
        if (open && triggerRef.current && contentRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const contentRect = contentRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let top = 0;
            let left = 0;

            // Calculate position based on side
            switch (side) {
                case 'top':
                    top = triggerRect.top - contentRect.height - sideOffset;
                    break;
                case 'bottom':
                    top = triggerRect.bottom + sideOffset;
                    break;
                case 'left':
                    left = triggerRect.left - contentRect.width - sideOffset;
                    break;
                case 'right':
                    left = triggerRect.right + sideOffset;
                    break;
            }

            // Calculate position based on align for vertical sides
            if (side === 'top' || side === 'bottom') {
                switch (align) {
                    case 'start':
                        left = triggerRect.left + alignOffset;
                        break;
                    case 'center':
                        left = triggerRect.left + triggerRect.width / 2 - contentRect.width / 2 + alignOffset;
                        break;
                    case 'end':
                        left = triggerRect.right - contentRect.width + alignOffset;
                        break;
                }
            }

            // Calculate position based on align for horizontal sides
            if (side === 'left' || side === 'right') {
                switch (align) {
                    case 'start':
                        top = triggerRect.top + alignOffset;
                        break;
                    case 'center':
                        top = triggerRect.top + triggerRect.height / 2 - contentRect.height / 2 + alignOffset;
                        break;
                    case 'end':
                        top = triggerRect.bottom - contentRect.height + alignOffset;
                        break;
                }
            }

            // Collision detection and adjustment
            const adjustedLeft = Math.max(
                collisionPadding,
                Math.min(left, viewportWidth - contentRect.width - collisionPadding)
            );
            const adjustedTop = Math.max(
                collisionPadding,
                Math.min(top, viewportHeight - contentRect.height - collisionPadding)
            );

            setPosition({ top: adjustedTop, left: adjustedLeft });
        }
    }, [open, side, align, sideOffset, alignOffset, collisionPadding]);

    // Initial position calculation — `useLayoutEffect` (vs `useEffect`) so the
    // measurement and re-position happen synchronously between the DOM mutation
    // and the browser's paint. With `useEffect` the portal would render once
    // at the initial `{top: 0, left: 0}` state (top-left corner of the viewport)
    // and then jump to its calculated spot a frame later — visible as a flash
    // on large content like an emoji picker (~350x420px). Layout effect closes
    // that window so the user only ever sees the content at its final position.
    React.useLayoutEffect(() => {
        calculatePosition();
    }, [calculatePosition]);

    // Add scroll and resize event listeners
    React.useEffect(() => {
        if (!open) return;

        const handleScrollOrResize = () => {
            calculatePosition();
        };

        window.addEventListener('scroll', handleScrollOrResize, true); // Use capture for all scroll events
        window.addEventListener('resize', handleScrollOrResize);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
        };
    }, [open, calculatePosition]);

    if (!open) return null;

    if (isMobile && showBottomModalOnMobile) {
        return createPortal(
            <MobileBottomModal
                onClickAway={() => setOpen(false)}
                zIndex={zIndex}
                className={clsx(className, mobileBottomModalProps?.className)}
                backdropClassName={mobileBottomModalProps?.backdropClassName}
            >
                {children}
            </MobileBottomModal>,
            document.body
        );
    }

    return createPortal(
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <div
                ref={contentRef}
                className={clsx(styles.root, className, {
                    [styles.noPaddingBottom]: hasFooter
                })}
                style={{
                    top: position.top,
                    left: position.left,
                    zIndex,
                    minWidth: minWidth ? `${minWidth}px` : undefined,
                    maxWidth: maxWidth ? `${maxWidth}px` : undefined,
                    maxHeight: maxHeight ? `${maxHeight}px` : undefined
                }}
                role="dialog"
                aria-modal="true"
            >
                {children}
            </div>
        </ClickAwayListener>,
        document.body
    );
};
